// Contas da simulação de financiamento do site (SAC e Price). Estimativa: não inclui seguros
// (MIP/DFI), taxa de administração nem correção pela TR — o banco informa o valor final.

export const ENTRADA_MINIMA = 0.2;          // 20%: o mínimo usual (SFH/Caixa); bancos privados pedem 20–30%
export const COMPROMETIMENTO_RENDA = 0.3;   // parcela até 30% da renda bruta familiar
export const PRAZOS = [240, 300, 360, 420]; // meses (420 = 35 anos, o máximo usual)

/** Taxa anual efetiva (%) → mensal equivalente (fração). 12% a.a. → 0,9489% a.m. */
export function taxaMensal(taxaAnualPct) {
  return (1 + Number(taxaAnualPct) / 100) ** (1 / 12) - 1;
}

/** Price: parcela fixa. */
export function price(valor, taxaAnualPct, meses) {
  const i = taxaMensal(taxaAnualPct);
  if (valor <= 0 || meses <= 0) return { primeira: 0, ultima: 0, total: 0, juros: 0 };
  const parcela = i === 0 ? valor / meses : (valor * i) / (1 - (1 + i) ** -meses);
  const total = parcela * meses;
  return { primeira: parcela, ultima: parcela, total, juros: total - valor };
}

/** SAC: amortização constante, parcela cai todo mês. */
export function sac(valor, taxaAnualPct, meses) {
  const i = taxaMensal(taxaAnualPct);
  if (valor <= 0 || meses <= 0) return { primeira: 0, ultima: 0, total: 0, juros: 0 };
  const amortizacao = valor / meses;
  // Juros somam sobre o saldo, que cai linearmente: soma = i * A * (n + n-1 + … + 1).
  const juros = i * amortizacao * (meses * (meses + 1)) / 2;
  return { primeira: amortizacao + valor * i, ultima: amortizacao * (1 + i), total: valor + juros, juros };
}

export function simular({ valorImovel, entrada, taxaAnualPct, meses, sistema = 'SAC', renda }) {
  const imovel = Math.max(0, Number(valorImovel) || 0);
  const ent = Math.min(imovel, Math.max(0, Number(entrada) || 0));
  const financiado = imovel - ent;
  const conta = sistema === 'PRICE' ? price : sac;
  const r = conta(financiado, taxaAnualPct, meses);
  const rendaMinima = r.primeira / COMPROMETIMENTO_RENDA;
  const rendaInformada = Number(renda) || 0;
  return {
    imovel, entrada: ent, financiado, ...r,
    percentualEntrada: imovel ? ent / imovel : 0,
    entradaMinima: imovel * ENTRADA_MINIMA,
    entradaAbaixoDoMinimo: imovel > 0 && ent < imovel * ENTRADA_MINIMA,
    rendaMinima,
    comprometimento: rendaInformada ? r.primeira / rendaInformada : null,
  };
}

/** Linhas de "sugestão de entrada": quanto fica a 1ª parcela com 20%, 30%, 40% e 50% de entrada. */
export function sugestoesEntrada({ valorImovel, taxaAnualPct, meses, sistema }) {
  return [0.2, 0.3, 0.4, 0.5].map((p) => ({
    percentual: p,
    ...simular({ valorImovel, entrada: valorImovel * p, taxaAnualPct, meses, sistema }),
  }));
}

/** Valor do imóvel que cabe na renda: maior financiamento com 1ª parcela ≤ 30% da renda, + entrada. */
export function imovelQueCabe({ renda, entrada, taxaAnualPct, meses, sistema = 'SAC' }) {
  const parcelaMax = (Number(renda) || 0) * COMPROMETIMENTO_RENDA;
  if (parcelaMax <= 0) return 0;
  const i = taxaMensal(taxaAnualPct);
  const financiavel = sistema === 'PRICE'
    ? (i === 0 ? parcelaMax * meses : parcelaMax * (1 - (1 + i) ** -meses) / i)
    : parcelaMax / (1 / meses + i);
  return financiavel + (Number(entrada) || 0);
}

export const moeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
